// Design tweaks live in index.html so the visual editor can keep rewriting them there.
const TWEAK_DEFAULTS = window.TWEAK_DEFAULTS || { accent: '#ff5a3c', theme: 'light' };

// Pick readable ink color over any accent.
function inkOn(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return '#14121c';
  const r = n >> 16 & 255,g = n >> 8 & 255,b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000 ? '#14121c' : '#ffffff';
}

const LOADERS = [
{ n: 'FORGE', ver: '1.7 – 1.21', icon: 'forge', soon: false },
{ n: 'NEOFORGE', ver: '1.20.4+', icon: 'neoforge', soon: false },
{ n: 'FABRIC', ver: '1.14+', icon: 'fabric', soon: false },
{ n: 'QUILT', ver: '1.18.2+', icon: 'quilt', soon: false },
{ n: 'THUNDERSTORE', ver: 'all games', icon: 'thunderstore', soon: true }];


// Cloudflare Turnstile, explicit render so the widget mounts wherever this
// component does and hands its token up through onToken.
function TurnstileWidget({ sitekey, onToken }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    let cancelled = false;
    let id = null;
    const render = () => {
      if (cancelled || !ref.current || !window.turnstile || id !== null) return;
      id = window.turnstile.render(ref.current, {
        sitekey,
        callback: (t) => onToken(t),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    };
    if (window.turnstile) {
      render();
    } else {
      window.__mpfTurnstileReady = render;
      if (!document.getElementById('cf-turnstile-script')) {
        const sc = document.createElement('script');
        sc.id = 'cf-turnstile-script';
        sc.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__mpfTurnstileReady&render=explicit';
        sc.async = true;
        document.head.appendChild(sc);
      }
    }
    return () => {
      cancelled = true;
      if (id !== null && window.turnstile) { try { window.turnstile.remove(id); } catch (_e) {} }
    };
  }, [sitekey]);
  return <div ref={ref} className="turnstile-slot" />;
}

function App() {
  const t = TWEAK_DEFAULTS;

  // Tiny hash router: '' = home, '#setup' = setup, '#spawning' = spawn
  // screen, '#waitlist' = the pre-launch signup screen.
  const hashToScreen = (h) =>
    h === '#waitlist' ? 'waitlist' :
    h === '#setup' ? 'setup' : h === '#spawning' ? 'spawning' : 'home';
  const [screen, setScreen] = React.useState(
    () => typeof window !== 'undefined' ? hashToScreen(window.location.hash) : 'home'
  );
  React.useEffect(() => {
    const onHash = () => setScreen(hashToScreen(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = (s) => {
    window.location.hash = s === 'home' ? '' : '#' + s;
    window.scrollTo({ top: 0 });
  };

  // Setup state
  const [scope, setScope] = React.useState('full');
  const [notes, setNotes] = React.useState('');
  // Public config from the Worker. It injects window.__MPF_CONFIG into the
  // page, so the very first render already knows whether the launch gate is
  // up and the hero CTA never flashes the wrong label; /api/config is the
  // fallback for a page that reached the browser without passing through
  // the Worker. Both carry the same two fields.
  //   coming_soon       — pre-launch: Start becomes "get notified" and the
  //                       setup screen is replaced by the waitlist screen.
  //   turnstile_site_key — when set, Start waits for a Turnstile token.
  const injectedConfig = typeof window !== 'undefined' ? window.__MPF_CONFIG : null;
  const [comingSoon, setComingSoon] = React.useState(
    () => !!(injectedConfig && injectedConfig.coming_soon)
  );
  const [turnstileKey, setTurnstileKey] = React.useState(
    () => (injectedConfig && injectedConfig.turnstile_site_key) || null
  );
  const [turnstileToken, setTurnstileToken] = React.useState('');
  React.useEffect(() => {
    if (injectedConfig) return;
    fetch('/api/config').then((r) => r.json()).then((cfg) => {
      setComingSoon(!!(cfg && cfg.coming_soon));
      setTurnstileKey(cfg && cfg.turnstile_site_key ? cfg.turnstile_site_key : null);
    }).catch(() => setTurnstileKey(null));
  }, []);
  const [starting, setStarting] = React.useState(false);
  const [spawnError, setSpawnError] = React.useState('');

  // Direct navigation / browser-back onto #spawning with no spawn in
  // flight makes no sense — send the user to setup instead.
  React.useEffect(() => {
    if (screen === 'spawning' && !starting && !spawnError) {
      window.location.replace('#setup');
    }
  }, [screen, starting, spawnError]);
  // Pre-launch, setup and spawning are dead ends — the Worker refuses both
  // the upload and the spawn — so a bookmarked '#setup' lands on the
  // waitlist instead of on a dropzone that cannot go anywhere.
  React.useEffect(() => {
    if (comingSoon && (screen === 'setup' || screen === 'spawning')) {
      window.location.replace('#waitlist');
    }
  }, [comingSoon, screen]);
  // One GA4 pageview per screen the visitor actually lands on. The two
  // redirect effects above replace a screen before it renders; those
  // transient states are skipped so only the destination is counted.
  // mpfTrack (from /analytics.js) is inert when GA4 is off.
  const lastTracked = React.useRef('');
  React.useEffect(() => {
    if (screen === 'spawning' && !starting && !spawnError) return;
    if (comingSoon && (screen === 'setup' || screen === 'spawning')) return;
    const path = screen === 'home' ? '/' : '/' + screen;
    if (path === lastTracked.current) return;   // a spawn error re-renders the same screen
    lastTracked.current = path;
    if (window.mpfTrack) window.mpfTrack.pageview(path);
  }, [screen, starting, spawnError, comingSoon]);
  const [packFile, setPackFile] = React.useState(null);
  const [uploadPct, setUploadPct] = React.useState(0);   // 0..100
  // 'idle' | 'uploading' | 'ready' | 'error'
  const [uploadState, setUploadState] = React.useState('idle');
  const [uploadError, setUploadError] = React.useState('');
  const [uploadKey, setUploadKey] = React.useState(null);
  const [uploadSid, setUploadSid] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const xhrRef = React.useRef(null);

  const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

  const fmtSize = (b) => {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1024 / 1024).toFixed(1) + ' MB';
  };

  const resetUpload = () => {
    if (xhrRef.current) { try { xhrRef.current.abort(); } catch (_e) {} xhrRef.current = null; }
    setPackFile(null);
    setUploadPct(0);
    setUploadState('idle');
    setUploadError('');
    setUploadKey(null);
    setUploadSid(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Begin upload as soon as a file is picked. We use XHR (not fetch) so we
  // can read xhr.upload.onprogress and drive the progress bar — fetch()
  // doesn't expose upload progress.
  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setPackFile(file);
      setUploadState('error');
      setUploadError(`Too large (${fmtSize(file.size)}) — max 500 MB`);
      return;
    }
    setPackFile(file);
    setUploadPct(0);
    setUploadError('');
    setUploadState('uploading');
    setUploadKey(null);
    setUploadSid(null);

    try {
      const presignResp = await fetch('/api/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          size: file.size,
          content_type: file.type || 'application/zip',
        }),
      });
      if (!presignResp.ok) {
        const txt = await presignResp.text();
        // Same as the spawn path: a gated upload means pre-launch, not a
        // broken upload. Send them to the waitlist.
        if (presignResp.status === 503 && txt.indexOf('coming_soon') >= 0) {
          setComingSoon(true);
          resetUpload();
          go('waitlist');
          return;
        }
        throw new Error(`upload-url ${presignResp.status}: ${txt.slice(0, 200)}`);
      }
      const presign = await presignResp.json();

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            // Don't let the bar hit 100% until the server actually
            // confirms — otherwise it sits "full" while we wait for
            // the response and feels broken.
            setUploadPct(Math.min(pct, 99));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else {
            const m = /<Message>([^<]+)<\/Message>/i.exec(xhr.responseText || '');
            reject(new Error(m ? m[1] : `R2 returned ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('network error during upload'));
        xhr.onabort = () => reject(new Error('upload cancelled'));
        xhr.open('PUT', presign.url);
        xhr.send(file);
      });

      xhrRef.current = null;
      setUploadPct(100);
      setUploadKey(presign.key);
      setUploadSid(presign.sid);
      setUploadState('ready');
    } catch (err) {
      xhrRef.current = null;
      setUploadState('error');
      setUploadError(err.message || String(err));
    }
  };

  // The "Start the fix" click — file is already on R2, so this just spawns
  // the session. We POST via fetch (with Accept: application/json so the
  // Worker answers {redirect} instead of a 303) so a failed spawn can be
  // shown in-page instead of dumping the Worker's error text on a blank tab.
  // Waitlist (pre-launch). POSTs to the Worker, which stores one R2 object
  // per address. 'already' is the server telling us this address was on the
  // list before, which is worth saying rather than silently re-thanking.
  const [wlEmail, setWlEmail] = React.useState('');
  const [wlTrap, setWlTrap] = React.useState('');
  const [wlState, setWlState] = React.useState('idle'); // idle | sending | joined | already
  const [wlError, setWlError] = React.useState('');
  const wlDone = wlState === 'joined' || wlState === 'already';

  const joinWaitlist = async () => {
    if (wlState === 'sending') return;
    // A filled honeypot is a script, not a person. Answer like it worked.
    if (wlTrap) { setWlState('joined'); return; }
    const email = wlEmail.trim();
    if (email.indexOf('@') < 1 || email.indexOf('.', email.indexOf('@')) < 0) {
      setWlError("That doesn't look like an email address.");
      return;
    }
    setWlState('sending');
    setWlError('');
    try {
      const resp = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'landing' }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          resp.status === 429
            ? 'Slow down a moment and try again.'
            : (body && body.detail) || `HTTP ${resp.status}`
        );
      }
      setWlState(body && body.already ? 'already' : 'joined');
      if (window.mpfTrack) window.mpfTrack.event('waitlist_signup', { repeat: body && body.already ? 'yes' : 'no' });
    } catch (err) {
      setWlError(err && err.message ? err.message : String(err));
      setWlState('idle');
    }
  };

  const startFix = async () => {
    if (starting) return;
    if (uploadState !== 'ready' || !uploadKey || !uploadSid) return;
    setStarting(true);
    setSpawnError('');
    go('spawning');
    const fd = new FormData();
    fd.append('mode', scope === 'full' ? 'multiplayer' : 'client');
    fd.append('memory_preset', '4g');
    fd.append('goal', notes);
    if (turnstileKey) fd.append('cf-turnstile-response', turnstileToken);
    fd.append('sid', uploadSid);
    fd.append('upload_key', uploadKey);
    fd.append('pack_name', (packFile && packFile.name ? packFile.name : 'modpack').replace(/\.(mrpack|zip)$/i, ''));
    try {
      const resp = await fetch('/sessions', {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const body = await resp.json();
          // The launch gate closed under a stale tab. Show the waitlist
          // rather than a spawn error nobody can act on.
          if (body && body.coming_soon) {
            setComingSoon(true);
            setStarting(false);
            go('waitlist');
            return;
          }
          if (body && body.detail) detail = String(body.detail);
        } catch (_e) {
          try { const txt = await resp.text(); if (txt) detail = txt.slice(0, 300); } catch (_e2) {}
        }
        throw new Error(detail);
      }
      let redirect = `/session/${uploadSid}/`;
      try {
        const body = await resp.json();
        if (body && body.redirect) redirect = body.redirect;
      } catch (_e) {}
      window.location.assign(redirect);
    } catch (err) {
      setSpawnError(err && err.message ? err.message : String(err));
      if (turnstileKey) { setTurnstileToken(''); try { window.turnstile && window.turnstile.reset(); } catch (_e) {} }
      setStarting(false);
    }
  };
  const SCOPE_OPTS = [
  { id: 'full',   glyph: '⌬', pn: '2P+', tag: '2+ PLAYERS', name: 'MULTIPLAYER',  recommended: true,  desc: 'Client-side, Server-side, and joining a server. Catches bugs where Client-side works but you can\'t connect to the server.' },
  { id: 'client', glyph: '◈', pn: '1P',  tag: '1 PLAYER',   name: 'SINGLEPLAYER', recommended: false, desc: 'Probably won\'t work in Multiplayer. Only checks the game in Singleplayer.' }];

  // Arrow keys to switch mode.
  React.useEffect(() => {
    if (screen !== 'setup') return;
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setScope((cur) => SCOPE_OPTS.find((o) => o.id !== cur).id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screen]);


  React.useEffect(() => {
    // light theme uses dark ink for accent contrast; dark theme uses near-black on light accents
    document.documentElement.style.setProperty('--accent', t.accent);
    document.documentElement.style.setProperty('--accent-ink', inkOn(t.accent));
    document.body.dataset.theme = t.theme;
  }, [t.accent, t.theme]);

  return (
    <>
        <div className="wrap">

          {screen === 'spawning' ?
        <section className="spawn">
              {spawnError ?
              <>
                <h2 className="spawn-title">SPAWN FAILED</h2>
                <p className="spawn-sub">
                  We couldn't start a <b>game environment</b> for your modpack. Your upload is still attached — go back and try again.
                </p>
                <div className="spawn-error" role="alert">{spawnError}</div>
                <button className="back-btn" onClick={() => go('setup')}>◀ BACK TO SETUP</button>
              </> :
              <>
                <h2 className="spawn-title">SPAWNING SESSION</h2>
                <p className="spawn-sub">
                  Reserving a clean <b>game environment</b> for your modpack. Hold tight — this only takes a moment.
                </p>
                <div className="spawn-arrows" aria-hidden="true" />
                <p className="spawn-tip">
                  <b>Tip:</b> your results page and downloads stay available for <b>24 hours</b> after the fix finishes, so bookmark this tab or copy the link once it's done.
                </p>
              </>}
            </section> :
          screen === 'waitlist' ?
        <section className="setup">
              <div className="setup-top">
                <button className="back-btn" onClick={() => go('home')}>◀ BACK</button>
              </div>

              <h2>READY SOON!</h2>
              <p className="lede">
                Hi! <b>Modpack Fixer</b> isn't done yet. I'm just one guy working on this, so it's going to take some time before it's fully ready.  
              </p>
              <p className="lede">
                What you can do is <b>sign up</b> to get a <b>notification</b> when it's done. That way you don't miss it, and I get some motivation to complete this faster.
              </p>

              <div className="field">
                <div className="label">
                  <span>EMAIL</span>
                </div>
                <div className={'ta-wrap' + (wlDone ? ' ok' : '')}>
                  <div className="corner" />
                  <input
                    className="wl-trap"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={wlTrap}
                    onChange={(e) => setWlTrap(e.target.value)} />
                  <input
                    className="email-in"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    aria-label="Email address"
                    maxLength={254}
                    value={wlEmail}
                    placeholder="you@example.com"
                    onChange={(e) => {
                      setWlEmail(e.target.value);
                      setWlError('');
                      // Editing a confirmed address starts a fresh signup —
                      // that is how a second address gets added.
                      if (wlDone) setWlState('idle');
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') joinWaitlist(); }} />
                </div>
                {(wlError || wlDone) && (
                  <div
                    className={'wl-msg ' + (wlError ? 'err' : 'ok')}
                    role={wlError ? 'alert' : 'status'}
                  >
                    {wlError ? wlError :
                      wlState === 'already' ?
                        <><b>✔ ALREADY ON IT</b>That address was on the list already.</> :
                        <><b>✔ YOU'RE ON THE LIST</b>I'll send you an email when it's done.</>}
                  </div>
                )}
              </div>

              <div className="setup-actions">
                <span className="hint"></span>
                {(() => {
                  const ready = !wlDone && wlState !== 'sending' && wlEmail.trim().length > 0;
                  const label = wlState === 'sending' ? 'SENDING…' : wlDone ? '✔ SIGNED UP' : 'NOTIFY ME';
                  return (
                    <span className={'pixel-btn-wrap' + (ready ? '' : ' muted')}>
                      <button
                        className={'pixel-btn' + (ready ? '' : ' muted')}
                        type="button"
                        onClick={joinWaitlist}
                        disabled={!ready}
                      >
                        {!wlDone && <span className="tri-up">▶</span>} {label}
                      </button>
                    </span>
                  );
                })()}
              </div>
            </section> :
          screen === 'setup' ?
        <section className="setup">
              <div className="setup-top">
                <button className="back-btn" onClick={() => go('home')}>◀ BACK</button>
              </div>

              <h2>SETUP</h2>

              {/* Dropzone (top of the screen) */}
              <div className="field" style={{ marginTop: '20px' }}>
                <div className="label">
                  <span>MODPACK</span>
                  <span className="req">REQUIRED · MAX 500 MB</span>
                </div>

                <div
                  className={
                    'drop'
                    + (dragOver ? ' dragging' : '')
                    + (uploadState === 'uploading' ? ' uploading' : '')
                    + (uploadState === 'ready' ? ' ready' : '')
                    + (uploadState === 'error' ? ' error' : '')
                    + (packFile && uploadState === 'idle' ? ' has-file' : '')
                  }
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                    if (f) pickFile(f);
                  }}
                >
                  <div className="corner" />
                  <div className="drop-glyph" aria-hidden="true" />
                  {!packFile && (
                    <>
                      <div className="drop-title">
                        {dragOver ? 'DROP IT IN' : 'DROP YOUR MODPACK HERE'}
                      </div>
                      <div className="drop-sub">
                        <b>.mrpack</b>, <b>CurseForge zip</b>, or a zipped <b>Prism / .minecraft</b> instance.
                      </div>
                    </>
                  )}
                  {packFile && (
                    <>
                      <div className="drop-title">
                        {uploadState === 'uploading' && 'UPLOADING'}
                        {uploadState === 'ready' && 'PACK READY'}
                        {uploadState === 'error' && 'UPLOAD FAILED'}
                        {uploadState === 'idle' && 'PACK SELECTED'}
                      </div>
                      <div className="drop-file" onClick={(e) => e.stopPropagation()}>
                        <span className="fname" title={packFile.name}>{packFile.name}</span>
                        <span className="fsize">{fmtSize(packFile.size)}</span>
                        <button
                          type="button"
                          className="fclear"
                          aria-label="Remove file"
                          onClick={(e) => { e.stopPropagation(); resetUpload(); }}
                        >✕</button>
                      </div>
                      {uploadState === 'error' && (
                        <div className="drop-sub" style={{ color: 'var(--accent)' }}>{uploadError}</div>
                      )}
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mrpack,.zip,application/zip,application/x-zip-compressed,application/octet-stream"
                    style={{ display: 'none' }}
                    onChange={(e) => pickFile(e.target.files && e.target.files[0])} />
                </div>

                {/* ── 2. Progress bar ─────────────────────────────── */}
                {packFile && uploadState !== 'error' && (() => {
                  // Snap the visual fill to 5% cells so it grows in pixel chunks,
                  // not as a smooth bar. The textual % stays exact.
                  const snapped = uploadState === 'ready'
                    ? 100
                    : Math.floor(uploadPct / 5) * 5;
                  return (
                    <div
                      className={
                        'progress '
                        + (uploadState === 'ready' ? 'done armed'
                          : uploadState === 'uploading' ? 'armed'
                          : 'idle')
                      }
                    >
                      <div className="progress-rail">
                        <div className="progress-fill" style={{ width: snapped + '%' }} />
                        <div className="progress-cells" />
                      </div>
                      <div className="progress-pct">
                        {uploadState === 'ready' ? 'READY' : uploadPct + '%'}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Mode select */}
              <div className="field">
                <div className="label">
                  <span>MODE</span>
                  <span className="req">REQUIRED</span>
                </div>
                <div className="modes-box">
                  <div className="modes">
                    {SCOPE_OPTS.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className="mode"
                        data-on={scope === o.id ? '1' : '0'}
                        onClick={() => setScope(o.id)}>
                        <span className="pointer">▶</span>
                        <div className="name-row">
                          <div className="name">{o.name}</div>
                          {o.recommended && <span className="badge-rec">★ RECOMMENDED</span>}
                        </div>
                        <div className="tag-line">{o.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── 4. Notes ────────────────────────────────────────── */}
              <div className="field">
                <div className="label">
                  <span>ADDITIONAL INFO</span>
                  <span className="opt">OPTIONAL</span>
                </div>
                <div className="help">Anything you want the AI to know. Only needed if you have a VERY specific issue.</div>
                <div className="ta-wrap">
                  <div className="corner" />
                  <textarea className="ta"
              value={notes}
              maxLength={2000}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="My game crashes whenever I open a chest???" />
                </div>
              </div>

              {/* ── 5. Start action ─────────────────────────────────── */}
              <div className="setup-actions">
                <span className="hint">★ NO COINS REQUIRED · FREE TO USE FOREVER ★</span>
                {turnstileKey && <TurnstileWidget sitekey={turnstileKey} onToken={setTurnstileToken} />}
                {(() => {
                  const ready = uploadState === 'ready' && !starting && (!turnstileKey || !!turnstileToken);
                  let label;
                  if (starting) label = 'STARTING…';
                  else if (uploadState === 'uploading') label = `UPLOADING… ${uploadPct}%`;
                  else if (uploadState === 'error') label = 'UPLOAD FAILED';
                  else if (uploadState === 'ready') label = 'START THE FIX';
                  else label = 'WAITING FOR PACK';
                  return (
                    <span className={'pixel-btn-wrap' + (ready ? '' : ' muted')}>
                      <button
                        className={'pixel-btn' + (ready ? '' : ' muted')}
                        type="button"
                        onClick={startFix}
                        disabled={!ready}
                      >
                        <span className="tri-up">▶</span> {label}
                      </button>
                    </span>
                  );
                })()}
              </div>
            </section> :
        <>

          {/* Hero / title screen */}
          <section className="hero">
            <div className="press-start">— PRESS START —</div>
            <h1>
              <span className="stack"></span>
              <span className="stack">MODPACK</span>
              <span className="stack" style={{ color: 'var(--accent)' }}>FIXER</span>
            </h1>

            <p className="sub">Upload your <b>broken modpack</b>, download a <b>working one</b>

            </p>

            <div className="cta-row" style={{ justifyContent: "center", alignItems: "stretch" }}>
              <span className="pixel-btn-wrap"><button className="pixel-btn" onClick={() => go(comingSoon ? 'waitlist' : 'setup')}><span className="tri-up">▶</span> {comingSoon ? "GET NOTIFIED WHEN IT'S READY" : 'PRESS START · FIX MY PACK'}</button></span>
            </div>

            <div className="free-tag">
              <span className="star">★</span> NO COINS REQUIRED · FREE TO USE FOREVER <span className="star">★</span>
            </div>



          </section>

          {/* Playable on */}
          <div className="section-rule">SUPPORTS</div>
          <div className="cabinets">
            {LOADERS.map((l) =>
            <div key={l.n} className={'cabinet' + (l.soon ? ' soon' : '')}>
                <div className="corner" />
                <div className="glyph">
                  <span className="icon-mask" style={{ '--icon': `url(icons/${l.icon}.svg)` }} />
                </div>
                <div className="name">{l.n}</div>
                <div className="ver">{l.ver}</div>
                <div className="badge">{l.soon ? '◇ SOON' : '◈ READY'}</div>
              </div>
            )}
          </div>
          <div className="playable-foot">
            The 4 big <b>Minecraft</b> mod loaders available now. Support for every <b>Thunderstore</b> game like Lethal Company, R.E.P.O., Risk of Rain 2, Content Warning is <b>coming soon.</b>
          </div>

          {/* How it works */}
          <div className="section-rule">HOW IT WORKS</div>
          <div className="combo">
            {[
            { n: '01', t: 'DROP YOUR PACK', d: 'Send the game folder with your modpack.' },
            { n: '02', t: 'AI LOOKS IT OVER', d: 'Checks your mods and what they don\'t like about each other. ' },
            { n: '03', t: 'PLAYS THE GAME', d: 'Boots the game. Joins a server. Reads the logs to find the issue.' },
            { n: '04', t: 'FIXES & TRIES AGAIN', d: 'Tweaks. Reboots. Tweaks again. Until the sucker works.' }].
            map((s, i, arr) =>
            <div key={s.n} className="step">
                <div className="corner" />
                <div className="n">{s.n}</div>
                <div className="title">{s.t}</div>
                <div className="desc">{s.d}</div>
                {i < arr.length - 1 && <div className="arrow">▶</div>}
              </div>
            )}
          </div>

          {/* Abilities */}
          <div className="section-rule">PERKS</div>
          <div className="abilities">
            {[
            { g: 'coins', t: '100% FREE', d: 'Powered 100% by ads. Requires no payment, account or sign-up. ' },
            { g: 'reload', t: 'SMART AI LOOP', d: 'Finds the issues through a loop of launching the game, reading the logs and tweaking.' },
            { g: 'coffee', t: 'HANDS-OFF', d: 'Press start. Walk away. Come back to a working modpack.' },
            { g: 'archive', t: 'KEEPS YOUR MODS', d: 'When a mod causes issues, it does everything in it\'s power to fix it through config, other mods, etc.' },
            { g: 'gamepad', t: 'ACTUALLY PLAYS IT', d: 'Checks the game AND the server. Verifies the connection so that Multiplayer works. ' },
            { g: 'cloud', t: 'NO INSTALL REQUIRED', d: 'Works fully in the browser. Just press start and send your modpack.' }].
            map((a) =>
            <div key={a.t} className="ability">
                <div className="glyph">
                  <span className="icon-mask" style={{ '--icon': `url(icons/${a.g}.svg)` }} />
                </div>
                <div className="title">{a.t}</div>
                <div className="desc">{a.d}</div>
              </div>
            )}
          </div>

          {/* Shared footer: markup + copy button come from /footer.js,
              styles from /footer.css, same as session.html/complete.html. */}
          <footer className="footer" ref={(el) => window.siteFooter?.(el)} />
          </>}
        </div>
      </>);

}

ReactDOM.createRoot(document.getElementById('app')).render(<App />);
